import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { nieuwUitStaat } from "../lib/slashopdrachten";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, Sparkles, Plus, Trash2, Download, Upload, Share2, Loader2, Check, Pencil, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { API } from "@/lib/api";
import MobileBottomNav, { BOTTOM_NAV_SPACER } from "../components/MobileBottomNav";
import AbilitiesLijst from "../components/AbilitiesLijst";
import { useTaal } from "@/contexts/TaalContext";
import { omschrijving, categorie } from "@/lib/galaxytekst";


const emptyForm = { name: "", description: "", category: "general", instructions: "" };

const PROVENANCE = {
  // Glasronde 0.9.189 vondst 4: de labels gaan door t(), dus ze staan hier in het Nederlands.
  galaxy: { label: "meegeleverd", trusted: true },
  custom: { label: "door jou geschreven", trusted: true },
  import: { label: "geïmporteerd bestand", trusted: false },
  p2p: { label: "van een andere node", trusted: false },
  "galaxy-registry": { label: "van een andere node", trusted: false },
};

/** Where a skill came from. A stranger's text is marked as such. */
function ProvenanceBadge({ source, untrusted }) {
  const { t } = useTaal();
  const info = PROVENANCE[source] || { label: source, trusted: !untrusted };
  return (
    <Badge
      variant={info.trusted ? "secondary" : "outline"}
      className={cn("text-xs", !info.trusted && "border-corona/50 text-corona")}
      title={info.trusted ? undefined : t("Deze skill is door iemand anders geschreven. Zijn herkomst is niet nagekeken.")}
    >
      {!info.trusted && <ShieldAlert className="h-3 w-3 mr-1" />}
      {PROVENANCE[source] ? t(info.label) : info.label}
    </Badge>
  );
}

/** What a skill asked for, and what it actually got. */
function ToolAccess({ skill, onGrant }) {
  const { t } = useTaal();
  const declared = skill.declared_tools || [];
  const granted = skill.granted_tools || [];
  if (!declared.length) {
    return skill.untrusted ? (
      <p className="text-xs text-muted-foreground mt-1">{t("Vraagt geen gereedschap.")}</p>
    ) : null;
  }
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      <span className="text-xs text-muted-foreground">{t("Gereedschap:")}</span>
      {declared.map(tool => {
        const ok = granted.includes(tool);
        return (
          <Badge
            key={tool}
            variant="outline"
            className={cn("text-xs", ok ? "border-ok/50 text-ok" : "text-muted-foreground")}
            title={ok ? t("Goedgekeurd") : t("Gevraagd maar niet goedgekeurd")}
          >
            {ok ? <Check className="h-3 w-3 mr-1" /> : <ShieldAlert className="h-3 w-3 mr-1" />}
            {tool}
          </Badge>
        );
      })}
      {skill.untrusted && (
        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => onGrant(skill)}>
          {t("Toegang nakijken")}
        </Button>
      )}
    </div>
  );
}

export default function SkillsPage() {
  const { t } = useTaal();
  const navigate = useNavigate();
  const [galaxy, setGalaxy] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(emptyForm);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [granting, setGranting] = useState(null);   // skill whose tool access is being reviewed
  const [grantChoice, setGrantChoice] = useState([]);

  const load = async () => {
    try {
      const [g, s] = await Promise.all([
        axios.get(`${API}/skills/galaxy`),
        axios.get(`${API}/skills`),
      ]);
      setGalaxy(g.data.skills || []);
      setInstalled(s.data || []);
    } catch {
      toast.error(t("Skills laden mislukt"));
    } finally {
      setLoading(false);
    }
  };

  // Alleen bij het openen; `load` meegeven zou hem bij elke render laten lopen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const installFromGalaxy = async (slug) => {
    setInstalling(slug);
    try {
      await axios.post(`${API}/skills/galaxy/${slug}/install`);
      toast.success(t("Skill geïnstalleerd"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Installeren mislukt"));
    } finally {
      setInstalling(null);
    }
  };

  const toggleSkill = async (skill, enabled) => {
    try {
      await axios.put(`${API}/skills/${skill.id}`, { enabled });
      setInstalled(prev => prev.map(s => s.id === skill.id ? { ...s, enabled } : s));
    } catch { toast.error(t("Skill bijwerken mislukt")); }
  };

  const deleteSkill = async (skill) => {
    try {
      await axios.delete(`${API}/skills/${skill.id}`);
      toast.success(t("Skill verwijderd"));
      await load();
    } catch { toast.error(t("Skill verwijderen mislukt")); }
  };

  const exportSkill = async (skill) => {
    try {
      const res = await axios.get(`${API}/skills/${skill.id}/export`);
      const blob = new Blob([res.data], { type: "text/markdown" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${skill.slug}.SKILL.md`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error(t("Exporteren mislukt")); }
  };

  const shareSkill = async (skill) => {
    try {
      const res = await axios.post(`${API}/skills/${skill.id}/share`);
      toast.success(`Published to the P2P network as ${res.data.published_id.slice(0, 12)}...`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Delen mislukt"));
    }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setEditorOpen(true); };
  // SL-a: /skill <naam> opent het formulier met de naam ingevuld.
  const locatie = useLocation();
  useEffect(() => {
    const naam = nieuwUitStaat(locatie.state);
    if (naam !== null) { setEditing(null); setForm({ ...emptyForm, name: naam }); setEditorOpen(true); }
  }, [locatie.key, locatie.state]);
  const openEdit = (skill) => {
    setEditing(skill);
    setForm({ name: skill.name, description: skill.description, category: skill.category, instructions: skill.instructions });
    setEditorOpen(true);
  };

  const saveSkill = async () => {
    if (!form.name.trim() || !form.instructions.trim()) return;
    try {
      if (editing) {
        await axios.put(`${API}/skills/${editing.id}`, form);
        toast.success(t("Skill bijgewerkt"));
      } else {
        await axios.post(`${API}/skills`, form);
        toast.success(t("Skill gesmeed"));
      }
      setEditorOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Opslaan mislukt"));
    }
  };

  const importSkill = async () => {
    if (!importText.trim()) return;
    try {
      await axios.post(`${API}/skills/import`, { skill_md: importText });
      toast.success(t("Skill geïmporteerd"));
      setImportOpen(false);
      setImportText("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Importeren mislukt"));
    }
  };

  const openGrant = (skill) => {
    setGranting(skill);
    setGrantChoice(skill.granted_tools || []);
  };

  const saveGrant = async () => {
    try {
      await axios.put(`${API}/skills/${granting.id}/tools`, { tools: grantChoice });
      toast.success(t("Toegang tot gereedschap bijgewerkt"));
      setGranting(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Toegang bijwerken mislukt"));
    }
  };

  const importSkillFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await axios.post(`${API}/skills/import-file`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("{f} geïmporteerd", { f: file.name }));
      setImportOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Importeren mislukt"));
    }
  };

  return (
    <div className={`min-h-screen flex flex-col bg-background ${BOTTOM_NAV_SPACER}`}>
      {/* Proef blok-k (19 okt): op 390 pixels schoof "Skill smeden" de pagina zijwaarts. */}
      <header className="flex flex-wrap items-center px-4 gap-3 pb-2 border-b min-h-12 pt-[var(--veilig-boven)] shrink-0" data-testid="skills-kop">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="tap-target h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="font-serif text-lg font-light">
          <Sparkles className="h-4 w-4 inline mr-2 text-[hsl(var(--accent))]" />
          {t("Galaxy-skills")}
        </h1>
        <div className="ml-auto flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="h-3 w-3 mr-1" />{t("Importeren")}
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-3 w-3 mr-1" />{t("Skill smeden")}
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 md:p-6 max-w-5xl w-full mx-auto">
        {/* MASTERPLAN 3ax-10 (post d): de vier abilities onder hun gbrain-naam. */}
        <AbilitiesLijst />
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <Tabs defaultValue="galaxy">
            <TabsList>
              <TabsTrigger value="galaxy">{t("Galaxy-catalogus")}</TabsTrigger>
              <TabsTrigger value="installed">{t("Mijn skills ({n})", { n: installed.length })}</TabsTrigger>
            </TabsList>

            <TabsContent value="galaxy" className="mt-4">
              <p className="text-sm text-muted-foreground mb-4">
                {t("Breid je AI uit met losse skills. Installeer met een klik, of smeed je eigen.")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {galaxy.map(s => (
                  <Card key={s.slug} className="flex flex-col">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{s.name}</CardTitle>
                        <Badge variant="outline" className="text-xs">{categorie(s.category, t)}</Badge>
                      </div>
                      <CardDescription className="text-xs">v{s.version}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1">
                      <p className="text-sm text-muted-foreground flex-1">{omschrijving(s, t)}</p>
                      {/* Outlined, not filled. The guide is explicit: never
                          flood the interface with orange, scarcity is the
                          power. Measured on glass at 1280 this grid put nine
                          solid Supernova bars on one screen, which makes the
                          colour mean nothing and buries Forge Skill, the one
                          action that is actually primary here. Same button,
                          same handler, same words. */}
                      <Button
                        className={cn(
                          "mt-4 w-full",
                          !s.installed &&
                            "border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))]"
                        )}
                        variant="outline"
                        disabled={s.installed || installing === s.slug}
                        onClick={() => installFromGalaxy(s.slug)}
                      >
                        {s.installed ? (<><Check className="h-3 w-3 mr-1" />{t("Geïnstalleerd")}</>)
                          : installing === s.slug ? (<Loader2 className="h-3 w-3 animate-spin" />)
                          : t("Installeren")}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="installed" className="mt-4">
              {installed.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-12">
                  {t("Nog geen skills geïnstalleerd. Installeer er een uit de Galaxy-catalogus of smeed je eigen.")}
                </p>
              ) : (
                <div className="space-y-3">
                  {installed.map(s => (
                    <Card key={s.id}>
                      <CardContent className="py-4 flex items-center gap-3 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium text-sm">{s.name}</h3>
                            <Badge variant="outline" className="text-xs">{categorie(s.category, t)}</Badge>
                            <ProvenanceBadge source={s.source} untrusted={s.untrusted} />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{omschrijving(s, t)}</p>
                          <ToolAccess skill={s} onGrant={openGrant} />
                        </div>
                        <div className="flex items-center gap-1">
                          <Switch checked={s.enabled} onCheckedChange={(v) => toggleSkill(s, v)} />
                          <Button variant="ghost" size="icon" className="tap-target h-8 w-8" onClick={() => openEdit(s)} title={t("Bewerken")}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="tap-target h-8 w-8" onClick={() => exportSkill(s)} title={t("SKILL.md exporteren")}>
                            <Download className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="tap-target h-8 w-8" onClick={() => shareSkill(s)} title={t("Delen op het P2P-netwerk")}>
                            <Share2 className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="tap-target h-8 w-8" onClick={() => deleteSkill(s)} title={t("Verwijderen")}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? t("Skill bewerken") : t("Een nieuwe skill smeden")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("Naam")}</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t("bijv. Offerteschrijver")} /></div>
            <div><Label>{t("Omschrijving")}</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t("Een regel: wat doet deze skill?")} /></div>
            <div><Label>{t("Categorie")}</Label><Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder={t("algemeen")} /></div>
            <div>
              <Label>{t("Instructies")}</Label>
              <Textarea rows={10} value={form.instructions} onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))} placeholder={t("De instructies die het model volgt als deze skill aan staat...")} />
            </div>
            <Button onClick={saveSkill} disabled={!form.name.trim() || !form.instructions.trim()} className="w-full">
              {editing ? t("Wijzigingen opslaan") : t("Skill smeden")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(granting)} onOpenChange={(open) => !open && setGranting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("Toegang tot gereedschap voor {naam}", { naam: granting?.name })}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t("Deze skill is door iemand anders geschreven. Zolang hij aan staat, kan de assistent alleen het gereedschap gebruiken dat je hier goedkeurt. De rest blijft onbereikbaar.")}
            </p>
            {(granting?.declared_tools || []).map(tool => (
              <label key={tool} className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch
                  checked={grantChoice.includes(tool)}
                  onCheckedChange={(v) => setGrantChoice(prev => v ? [...prev, tool] : prev.filter(t => t !== tool))}
                />
                <span className="font-mono text-xs">{tool}</span>
              </label>
            ))}
            {!(granting?.declared_tools || []).length && (
              <p className="text-sm text-muted-foreground">{t("Deze skill vraagt geen gereedschap.")}</p>
            )}
            <Button onClick={saveGrant} className="w-full">{t("Toegang opslaan")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("SKILL.md importeren")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t("Kies een SKILL.md-bestand, of plak er een hieronder. Het formaat wordt gedeeld met ander agentgereedschap, dus een skill van elders komt ongewijzigd binnen.")}
            </p>
            <Button variant="outline" className="w-full" onClick={() => document.getElementById("skill-file-input")?.click()}>
              <Upload className="h-3 w-3 mr-1" />{t("SKILL.md-bestand kiezen")}
            </Button>
            <input id="skill-file-input" type="file" accept=".md,text/markdown" className="hidden" onChange={importSkillFile} />
            <Textarea rows={10} value={importText} onChange={e => setImportText(e.target.value)} placeholder={"---\nname: My Skill\ndescription: ...\n---\n\nInstructions..."} />
            <Button onClick={importSkill} disabled={!importText.trim()} className="w-full">{t("Geplakte tekst importeren")}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <MobileBottomNav />
    </div>
  );
}
